import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const TRUELOG_BLUE = '#385CF2';
const TRUELOG_CYAN = '#0E9ED5';

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '28%']);
  const opacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.55], [1, 0.97]);

  return (
    <section
      ref={containerRef}
      className="relative flex min-h-[70vh] items-center overflow-hidden bg-slate-950 text-white"
    >
      <div className="absolute inset-0 z-0">
        <img
          src={`${process.env.PUBLIC_URL}/assets/images/hero-bg.jpg`}
          alt=""
          className="h-full w-full object-cover opacity-[0.15]"
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,92,242,0.28),transparent_38%),radial-gradient(circle_at_75%_20%,rgba(14,158,213,0.22),transparent_30%),linear-gradient(135deg,rgba(2,6,23,0.94),rgba(15,23,42,0.88)_45%,rgba(2,6,23,0.97))]" />
      </div>

      <motion.div
        style={{ y, opacity, scale }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-20 sm:px-6 lg:px-8 lg:pt-24"
      >
        <div className="mx-auto max-w-4xl text-center">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.08, ease: 'easeOut' }}
            className="font-display text-4xl font-bold leading-[1.06] tracking-[-0.04em] text-white sm:text-6xl lg:text-[4.25rem]"
          >
            One Partner. <span className="whitespace-nowrap">End-to-End</span> Global ICT Logistics
          </motion.h1>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.14, ease: 'easeOut' }}
            className="mt-4 font-display text-3xl font-semibold leading-tight tracking-[-0.02em] sm:text-4xl lg:text-[2.75rem]"
          >
            <span className="bg-gradient-to-r from-white via-[#8dcaf0] to-[#3b82f6] bg-clip-text text-transparent">
              Move global ICT at scale, without friction.
            </span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.28, ease: 'easeOut' }}
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link
              to="/services"
              className="inline-flex items-center justify-center rounded-2xl px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${TRUELOG_CYAN} 0%, ${TRUELOG_BLUE} 100%)` }}
            >
              Explore our Solutions
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/[0.08] px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur-xl transition-all duration-300 hover:border-white/25 hover:bg-white/[0.12] hover:text-white"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
